// extra-fx-color — color/palette effects
// Retro palette quantization, Cross-process, Blend mode overlay

const N = (v) => Number(v) || 0;

// ---- Retro palette quantization ----
const PALETTES = {
    'gameboy': [[15,56,15],[48,98,48],[139,172,15],[155,188,15]],
    'pico8': [[0,0,0],[29,43,83],[126,37,83],[0,135,81],[171,82,54],[95,87,79],[194,195,199],[255,241,232],[255,0,77],[255,163,0],[255,236,39],[0,228,54],[41,173,255],[131,118,156],[255,119,168],[255,204,170]],
    'c64': [[0,0,0],[255,255,255],[136,0,0],[170,255,238],[204,68,204],[0,204,85],[0,0,170],[238,238,119],[221,136,85],[102,68,0],[255,119,119],[51,51,51],[119,119,119],[170,255,102],[0,136,255],[187,187,187]],
    'nes': [[124,124,124],[0,0,252],[0,0,188],[68,40,188],[148,0,132],[168,0,32],[168,16,0],[136,20,0],[80,48,0],[0,120,0],[0,104,0],[0,88,0],[0,64,88],[0,0,0],[188,188,188],[0,120,248],[0,88,248],[104,68,252],[216,0,204],[228,0,88],[248,56,0],[228,92,16],[172,124,0],[0,184,0],[0,168,0],[0,168,68],[0,136,136],[248,248,248],[60,188,252],[104,136,252],[152,120,248],[248,120,248],[248,88,152],[248,120,88],[252,160,68],[248,184,0],[184,248,24],[88,216,84],[88,248,152],[0,232,216],[248,248,248],[164,228,252],[104,136,252],[104,136,252],[216,92,248],[248,88,152],[248,120,88],[252,160,68],[248,184,0],[184,248,24],[88,216,84],[88,248,152],[0,232,216]],
    'vga256': null, // generated below
    '1bit': [[0,0,0],[255,255,255]],
    'cga': [[0,0,0],[85,255,255],[255,85,255],[255,255,255]],
    'sepia': null, // generated
};
// Generate VGA 256 palette (6x6x6 color cube + grayscale)
(function() {
    const vga = [];
    for (let r = 0; r < 6; r++) for (let g = 0; g < 6; g++) for (let b = 0; b < 6; b++)
        vga.push([r*51, g*51, b*51]);
    for (let i = 0; i < 40; i++) vga.push([i*6, i*6, i*6]);
    PALETTES.vga256 = vga;
    // Sepia
    const sepia = [];
    for (let i = 0; i < 16; i++) {
        const v = i * 17;
        sepia.push([Math.min(255, v * 1.2), v * 0.9, v * 0.5]);
    }
    PALETTES.sepia = sepia;
})();

function findNearestColor(r, g, b, palette) {
    let best = 0, bestDist = Infinity;
    for (let i = 0; i < palette.length; i++) {
        const dr = r - palette[i][0], dg = g - palette[i][1], db = b - palette[i][2];
        const d = dr*dr + dg*dg + db*db;
        if (d < bestDist) { bestDist = d; best = i; }
    }
    return palette[best];
}

const retroPalette = async (canvas, ctx, paletteIdx) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const keys = ['gameboy', 'pico8', 'c64', 'nes', 'vga256', '1bit', 'cga', 'sepia'];
    const key = keys[Math.floor(N(paletteIdx)) % keys.length] || 'pico8';
    const palette = PALETTES[key];
    for (let i = 0; i < data.length; i += 4) {
        const [r, g, b] = findNearestColor(data[i], data[i+1], data[i+2], palette);
        data[i] = r; data[i+1] = g; data[i+2] = b;
    }
    ctx.putImageData(img, 0, 0);
};

// ---- Cross-process ----
const crossProcess = async (canvas, ctx, intensity) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const inten = N(intensity) / 100;
    for (let i = 0; i < data.length; i += 4) {
        let r = data[i] / 255, g = data[i+1] / 255, b = data[i+2] / 255;
        const lum = 0.299*r + 0.587*g + 0.114*b;
        // Shadows → blue
        if (lum < 0.5) {
            const t = (0.5 - lum) * 2 * inten;
            r = r * (1 - t * 0.3);
            g = g * (1 - t * 0.1);
            b = Math.min(1, b + t * 0.3);
        }
        // Highlights → yellow/orange
        else {
            const t = (lum - 0.5) * 2 * inten;
            r = Math.min(1, r + t * 0.2);
            g = Math.min(1, g + t * 0.15);
            b = b * (1 - t * 0.25);
        }
        // Boost saturation
        const avg = (r + g + b) / 3;
        r = Math.max(0, Math.min(1, avg + (r - avg) * (1 + inten * 0.5)));
        g = Math.max(0, Math.min(1, avg + (g - avg) * (1 + inten * 0.5)));
        b = Math.max(0, Math.min(1, avg + (b - avg) * (1 + inten * 0.5)));
        data[i] = r * 255; data[i+1] = g * 255; data[i+2] = b * 255;
    }
    ctx.putImageData(img, 0, 0);
};

// ---- Blend mode overlay ----
const blendMode = async (canvas, ctx, hue, mode) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const hVal = N(hue);
    const m = Math.floor(N(mode)); // 0=multiply,1=screen,2=overlay,3=difference,4=exclusion
    const [sr, sg, sb] = hslToRgb(hVal / 360, 0.8, 0.5);
    const srN = sr / 255, sgN = sg / 255, sbN = sb / 255;
    for (let i = 0; i < data.length; i += 4) {
        const r = data[i] / 255, g = data[i+1] / 255, b = data[i+2] / 255;
        let nr, ng, nb;
        if (m === 0) { nr = r * srN; ng = g * sgN; nb = b * sbN; }
        else if (m === 1) { nr = 1 - (1 - r) * (1 - srN); ng = 1 - (1 - g) * (1 - sgN); nb = 1 - (1 - b) * (1 - sbN); }
        else if (m === 2) {
            nr = r < 0.5 ? 2 * r * srN : 1 - 2 * (1 - r) * (1 - srN);
            ng = g < 0.5 ? 2 * g * sgN : 1 - 2 * (1 - g) * (1 - sgN);
            nb = b < 0.5 ? 2 * b * sbN : 1 - 2 * (1 - b) * (1 - sbN);
        }
        else if (m === 3) { nr = Math.abs(r - srN); ng = Math.abs(g - sgN); nb = Math.abs(b - sbN); }
        else { nr = r + srN - 2 * r * srN; ng = g + sgN - 2 * g * sgN; nb = b + sbN - 2 * b * sbN; }
        data[i] = Math.max(0, Math.min(255, nr * 255));
        data[i+1] = Math.max(0, Math.min(255, ng * 255));
        data[i+2] = Math.max(0, Math.min(255, nb * 255));
    }
    ctx.putImageData(img, 0, 0);
};
function hslToRgb(h, s, l) {
    let r, g, b;
    if (s === 0) { r = g = b = l; }
    else {
        const hue2rgb = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1/6) return p + (q - p) * 6 * t; if (t < 1/2) return q; if (t < 2/3) return p + (q - p) * (2/3 - t) * 6; return p; };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3); g = hue2rgb(p, q, h); b = hue2rgb(p, q, h - 1/3);
    }
    return [r * 255, g * 255, b * 255];
}

const descriptors = [
    { name: 'pal-retro', inputs: [
        { name: 'Palette', type: 'knob', min: 0, max: 7, step: 1, value: 1 }], fn: retroPalette },
    { name: 'pal-cross-process', inputs: [
        { name: 'Intensity', type: 'knob', min: 0, max: 100, step: 1, value: 60 }], fn: crossProcess },
    { name: 'pal-blend-mode', inputs: [
        { name: 'Hue', type: 'knob', min: 0, max: 360, step: 1, value: 200 },
        { name: 'Mode', type: 'knob', min: 0, max: 4, step: 1, value: 2 }], fn: blendMode },
];

const lib = {};
descriptors.forEach(d => lib[d.name] = d.fn);

module.exports = { descriptors, lib };
